import LivePreviewBase from "./live_preview_base"
import {
  applyTextStrokeToElements,
  applyFontSizeScale,
  applyTypographyToElements,
} from "../utils/text_treatment"
import { QUIZ_SHORT_TYPE_PAIRS } from "../services/quiz_short_type_pairs"

// Live-preview controller for the Quiz Short wizard.
//
// TYPE_PAIR_SPECS is de-mirrored (B11 typography de-mirror): the font data is
// authored ONCE in the shared TS source
// remotion-service/templates/src/shared/typography/quiz_short_pairs.ts →
// generated/typography/quiz_short.json, consumed here via
// ../services/quiz_short_type_pairs. The Ruby leg
// (app/components/shorts_wizard/quiz/live_preview_component.rb) consumes the same
// JSON, so the preview can never drift from the renderer.
//
// Mounted alongside `palette-live-preview`. Inherits event wiring +
// dispatch + value normalisation from LivePreviewBase.
export default class extends LivePreviewBase {
  static targets = [
    "root", "content", "question",
    "optionList", "optionChip", "optionBadge",
    "timerRing", "caption", "backgroundImage",
  ]

  // Preview displays at 320 / 1080 = 0.296× of the renderer canvas.
  static BASE_FONT_SIZES = { question: 56, option: 36 }
  static PREVIEW_SCALE = 320 / 1080

  static TYPE_PAIR_SPECS = { ...QUIZ_SHORT_TYPE_PAIRS }

  // Quiz Short color presets — mirror of COLOR_PRESETS in
  // app/components/shorts_wizard/quiz/style_step_component.rb.
  static COLOR_PRESETS = {
    classic: {
      card_background: "#1A1A2E", text_color: "#FFFFFF",
      correct_color: "#00FF88", incorrect_color: "#FF4444",
      timer_color: "#6366F1",
      option_background: "#2D2D44", option_selected_background: "#3D3D5C",
    },
    neon: {
      card_background: "#0D0D0D", text_color: "#FFFFFF",
      correct_color: "#39FF14", incorrect_color: "#FF073A",
      timer_color: "#00D4FF",
      option_background: "#1A1A1A", option_selected_background: "#2A2A2A",
    },
    warm: {
      card_background: "#2D1F1A", text_color: "#FFF5E1",
      correct_color: "#4CAF50", incorrect_color: "#FF6B6B",
      timer_color: "#FFB74D",
      option_background: "#3D2F2A", option_selected_background: "#4D3F3A",
    },
    ocean: {
      card_background: "#0A1929", text_color: "#E3F2FD",
      correct_color: "#69F0AE", incorrect_color: "#FF5252",
      timer_color: "#29B6F6",
      option_background: "#1A3344", option_selected_background: "#2A4354",
    },
  }

  static FIELD_BINDINGS = {
    type_pair:              { apply: "applyTypography" },
    italic_override:        { apply: "applyTypography" },
    font_size_scale:        { apply: "applyFontSize" },
    display_override:       { apply: "applyFontSize" },
    body_override:          { apply: "applyFontSize" },
    text_color:             { apply: "applyTextColor" },
    text_position:          { apply: "applyPosition" },
    text_shadow:            { apply: "applyTextShadow" },
    text_stroke_color:      { apply: "applyStroke" },
    text_stroke_width:      { apply: "applyStroke" },
    color_preset:           { apply: "applyColorPreset" },
    timer_duration_seconds: { apply: "applyTimerDuration" },
  }

  applyTypography() {
    const spec = this.constructor.TYPE_PAIR_SPECS[this.fieldValue("type_pair")]
    if (!spec) return
    applyTypographyToElements(
      {
        display: [this.questionTarget].filter(Boolean),
        body: [
          ...this.optionChipTargets,
          ...this.optionBadgeTargets,
          this.captionTarget,
        ].filter(Boolean),
      },
      spec,
      this.fieldValue("italic_override") || "auto",
    )
  }

  applyFontSize() {
    const ratio = this.constructor.PREVIEW_SCALE
    const globalScale = positiveOr(this.fieldValue("font_size_scale"), 1)
    const displayScale = this.readPositiveOverride("display_override") ?? globalScale
    const bodyScale    = this.readPositiveOverride("body_override")    ?? globalScale

    const displayPairs = []
    if (this.hasQuestionTarget) {
      displayPairs.push({ element: this.questionTarget, basePx: this.constructor.BASE_FONT_SIZES.question * ratio })
    }
    applyFontSizeScale(displayPairs, displayScale)

    const bodyPairs = []
    for (const chip of this.optionChipTargets) {
      bodyPairs.push({ element: chip, basePx: this.constructor.BASE_FONT_SIZES.option * ratio })
    }
    applyFontSizeScale(bodyPairs, bodyScale)
  }

  // Caption-style outline on the question only. Option chips skipped.
  applyStroke() {
    applyTextStrokeToElements(
      [this.questionTarget].filter(Boolean),
      {
        width: this.fieldValue("text_stroke_width"),
        color: this.fieldValue("text_stroke_color"),
      },
    )
  }

  applyTextColor({ value }) {
    if (!value) return
    if (this.hasContentTarget) this.contentTarget.style.color = value
    if (this.hasCaptionTarget) this.captionTarget.style.color = value
    if (this.hasTimerRingTarget) this.timerRingTarget.style.color = value
    for (const chip of this.optionChipTargets) chip.style.color = value
  }

  applyPosition({ value }) {
    if (!this.hasContentTarget) return
    this.contentTarget.style.justifyContent =
      value === "upper_third" ? "flex-start"
      : value === "lower_third" ? "flex-end"
      : "center"
  }

  applyTextShadow({ value }) {
    if (!this.hasContentTarget) return
    this.contentTarget.style.textShadow = value
      ? "0 2px 12px rgba(0,0,0,0.55)" : "none"
  }

  // Color preset tile → reflect swatches in the preview AND populate
  // the hidden style[*] inputs so saved data matches what the user sees.
  applyColorPreset({ event }) {
    const presetName = event.target.value
    const jsonAttr = event.target.dataset.colors
    const preset = this.constructor.COLOR_PRESETS[presetName] ||
      (jsonAttr ? safeParseJson(jsonAttr) : null)
    if (!preset) return

    if (this.hasTimerRingTarget) this.timerRingTarget.style.borderColor = preset.timer_color
    this.optionChipTargets.forEach((chip, idx) => {
      chip.style.background = preset.option_background
      // First chip is the "correct" demo highlight.
      chip.style.borderColor = idx === 0 ? preset.correct_color : "transparent"
    })
    this.optionBadgeTargets.forEach((badge, idx) => {
      badge.style.background = idx === 0
        ? preset.correct_color
        : preset.option_selected_background
    })

    if (preset.text_color) this.applyTextColor({ value: preset.text_color })
    const textColorInput = this.element.querySelector('[data-live-preview-field="text_color"]')
    if (textColorInput && preset.text_color) textColorInput.value = preset.text_color

    for (const key of Object.keys(preset)) {
      const hidden = this.element.querySelector(`input[type="hidden"][data-style-field="${key}"]`)
      if (hidden) hidden.value = preset[key]
    }
  }

  applyTimerDuration({ value }) {
    const n = parseInt(value, 10)
    if (!Number.isFinite(n)) return
    if (this.hasTimerRingTarget) this.timerRingTarget.textContent = String(n)
    if (this.hasCaptionTarget) {
      // Caption format is "Quiz Short · <N>s timer" — replace just the
      // numeric segment.
      const spans = this.captionTarget.querySelectorAll("span")
      const last = spans[spans.length - 1]
      if (last) last.textContent = `${n}s timer`
    }
  }

  readPositiveOverride(fieldName) {
    for (const el of this.fieldElements(fieldName)) {
      if (el.type === "range" && !el.disabled) {
        const v = parseFloat(el.value)
        return Number.isFinite(v) && v > 0 ? v : null
      }
    }
    return null
  }
}

function positiveOr(value, fallback) {
  if (value === null || value === undefined) return fallback
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

function safeParseJson(str) {
  try { return JSON.parse(str) } catch { return null }
}
